// =============================================
// Content Engine Logger — 3-tier logging + TXT collector
// VITE_LOG_LEVEL=production (default) → logPublic/logError/logWarn only
// VITE_LOG_LEVEL=debug → + logDebug (without FULL PROMPT / response metadata dumps)
// VITE_LOG_LEVEL=verbose → + logVerbose (OpenAI/SERP structure + full prompt/response dumps)
//
// verbose implies debug: isDebug stays true when LOG_LEVEL === "verbose".
// logVerbose gates on its own flag, so only "verbose" sees those entries.
//
// W Logger v2 (2026-04-18): wszystkie wpisy dual-write do IndexedDB (Dexie)
// przez @/services/logger/loggerStorage — globalny AppLogPanel czyta z IDB.
// Backward compat: wszystkie istniejące calle bez `category` → 'system'.
// =============================================

import { persistEntry, type LogCategory } from "@/services/logger/loggerStorage";

// Safe read — import.meta.env is Vite-only; w Node (tsx) użyjemy process.env fallback.
function readEnvLevel(): string {
  const fromVite =
    typeof import.meta !== "undefined" &&
    (import.meta as { env?: { VITE_LOG_LEVEL?: string } }).env?.VITE_LOG_LEVEL;
  const fromProcess =
    typeof process !== "undefined" ? process.env.VITE_LOG_LEVEL : undefined;
  // Runtime override via Settings UI (saved to localStorage)
  const fromLocalStorage =
    typeof window !== "undefined" ? localStorage.getItem("app_log_level") : null;
  return fromLocalStorage || fromVite || fromProcess || "production";
}

let LOG_LEVEL = readEnvLevel();
// verbose implies debug — isDebug=true dla "debug" i "verbose" (żeby UI toggle debug
// nadal widział DEBUG entries). isVerbose=true TYLKO dla "verbose" (logVerbose gate).
let isDebug = LOG_LEVEL === "debug" || LOG_LEVEL === "verbose";
let isVerbose = LOG_LEVEL === "verbose";

// Hot-reload log level on storage event (when user toggles dev mode in Settings)
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === "app_log_level") {
      LOG_LEVEL = readEnvLevel();
      isDebug = LOG_LEVEL === "debug" || LOG_LEVEL === "verbose";
      isVerbose = LOG_LEVEL === "verbose";
    }
  });
}

const LOG_PREFIX = "[CE]";

// Re-export LogCategory dla wygody konsumentów
export type { LogCategory } from "@/services/logger/loggerStorage";

// =============================================
// LOG COLLECTOR (debug only)
// Collects all logs per run into memory → localStorage → TXT export
// =============================================

interface LogEntry {
  ts: string;           // ISO timestamp
  level: "PUBLIC" | "DEBUG" | "ERROR" | "WARN";
  prefix: string;
  msg: string;
  data?: Record<string, unknown>;
}

interface LogRun {
  runId: string;
  meta: Record<string, unknown>;
  startedAt: string;
  endedAt?: string;
  entries: LogEntry[];
}

const LOCAL_STORAGE_KEY = "ce_log_runs";
// NO LIMITS — user manages cleanup manually via "Wyczysc logi" button
// WARNING: localStorage has ~5-10 MB limit per domain. Clear periodically.

// In-memory state (active runs)
let currentRunId: string | null = null;
let runs: Map<string, LogRun> = new Map();

// Load previous runs from localStorage on module init (debug only)
if (isDebug && typeof window !== "undefined") {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      const parsed: LogRun[] = JSON.parse(stored);
      for (const run of parsed) runs.set(run.runId, run);
    }
  } catch {
    // ignore parse errors
  }
}

// Hardcap defensywny — gdy bardzo dużo runów w pamięci, trzymamy tylko ostatnie
// MAX_RUNS_IN_STORAGE w localStorage. Pełna historia jest w IndexedDB (Dexie).
const MAX_RUNS_IN_STORAGE = 30;

function persistRuns(): void {
  if (!isDebug || typeof window === "undefined") return;

  // Sortuj od najnowszych, weź N najnowszych
  const sortedRuns = Array.from(runs.values()).sort((a, b) =>
    b.startedAt.localeCompare(a.startedAt),
  );

  // Auto-rotation: zaczynamy od MAX_RUNS_IN_STORAGE i schodzimy w dół przy QuotaExceededError
  let trySize = Math.min(sortedRuns.length, MAX_RUNS_IN_STORAGE);

  while (trySize > 0) {
    try {
      const slice = sortedRuns.slice(0, trySize);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(slice));
      return; // sukces
    } catch (err) {
      const isQuota =
        err instanceof Error &&
        (err.name === "QuotaExceededError" ||
          err.message.includes("exceeded the quota"));
      if (!isQuota) {
        console.warn("[CE] [Logger] persistRuns failed (non-quota):", err);
        return;
      }
      // Halve i spróbuj ponownie — drop najstarsze runs
      trySize = Math.floor(trySize / 2);
    }
  }

  // Nawet 1 run się nie zmieścił — wyczyść klucz całkowicie żeby nie blokować
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function appendEntry(entry: LogEntry, category: LogCategory = "system"): void {
  // Always persist to IndexedDB (visible in global AppLogPanel)
  // Debug entries persisted only when isDebug, public/error/warn always
  const shouldPersistToIDB =
    isDebug || entry.level === "PUBLIC" || entry.level === "ERROR" || entry.level === "WARN";
  if (shouldPersistToIDB) {
    void persistEntry({
      ts: entry.ts,
      level: entry.level,
      category,
      prefix: entry.prefix,
      msg: entry.msg,
      data: entry.data,
      runId: currentRunId || undefined,
    });
  }

  // Legacy in-memory + localStorage collector (only debug + active run)
  if (!isDebug || !currentRunId) return;
  const run = runs.get(currentRunId);
  if (!run) return;
  run.entries.push(entry);
}

/** Start a new log run. Only active in debug mode. */
export function startLogRun(runId: string, meta: Record<string, unknown> = {}): void {
  if (!isDebug) return;
  const run: LogRun = {
    runId,
    meta,
    startedAt: new Date().toISOString(),
    entries: [],
  };
  runs.set(runId, run);
  currentRunId = runId;
  appendEntry({
    ts: new Date().toISOString(),
    level: "PUBLIC",
    prefix: "RUN",
    msg: `START — ${JSON.stringify(meta)}`,
  });
}

/** End current log run. Persists to localStorage. */
export function endLogRun(summary?: Record<string, unknown>): void {
  if (!isDebug || !currentRunId) return;
  const run = runs.get(currentRunId);
  if (run) {
    run.endedAt = new Date().toISOString();
    appendEntry({
      ts: run.endedAt,
      level: "PUBLIC",
      prefix: "RUN",
      msg: `END${summary ? " — " + JSON.stringify(summary) : ""}`,
    });
    persistRuns();
  }
  currentRunId = null;
}

/** Clear all in-memory runs + localStorage. Used by "Wyczyść logi" button. */
export function clearLogRuns(): void {
  runs.clear();
  currentRunId = null;
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

/** Export a specific run as TXT string */
export function exportLogRunAsTxt(runId: string): string {
  const run = runs.get(runId);
  if (!run) return `No run found for ID: ${runId}`;

  const lines: string[] = [];
  lines.push("=".repeat(60));
  lines.push(`[CE] RUN LOG — ${runId}`);
  lines.push(`Started: ${run.startedAt}`);
  if (run.endedAt) lines.push(`Ended:   ${run.endedAt}`);
  lines.push(`Meta: ${JSON.stringify(run.meta)}`);
  lines.push(`Entries: ${run.entries.length}`);
  lines.push("=".repeat(60));
  lines.push("");

  for (const e of run.entries) {
    const time = e.ts.split("T")[1]?.slice(0, 12) || "";
    const levelPad = e.level.padEnd(6, " ");
    let line = `[${time}] [${levelPad}] [${e.prefix}] ${e.msg}`;
    if (e.data && Object.keys(e.data).length > 0) {
      // NO TRUNCATION — full data in TXT export
      const dataStr = Object.entries(e.data)
        .map(([k, v]) => {
          if (typeof v === "string") return `  ${k}: ${v}`;
          if (typeof v === "object" && v !== null) return `  ${k}: ${JSON.stringify(v, null, 2)}`;
          return `  ${k}: ${v}`;
        })
        .join("\n");
      line += "\n" + dataStr;
    }
    lines.push(line);
  }

  lines.push("");
  lines.push("=".repeat(60));
  lines.push(`END OF LOG — ${run.entries.length} entries`);
  lines.push("=".repeat(60));

  return lines.join("\n");
}

/** List all stored runs */
export function listLogRuns(): Array<{ runId: string; meta: Record<string, unknown>; startedAt: string; endedAt?: string; entryCount: number }> {
  return Array.from(runs.values())
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))
    .map((r) => ({
      runId: r.runId,
      meta: r.meta,
      startedAt: r.startedAt,
      endedAt: r.endedAt,
      entryCount: r.entries.length,
    }));
}

/** Trigger browser download of TXT for a specific run */
export function downloadLogRunAsTxt(runId: string, filename?: string): void {
  if (typeof window === "undefined") return;
  const txt = exportLogRunAsTxt(runId);
  const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `ce-log-${runId}-${new Date().toISOString().slice(0, 19).replace(/[:]/g, "-")}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Check if collector is active (for UI to show/hide buttons) */
export function isLogCollectorActive(): boolean {
  return isDebug;
}

/** Clear all stored runs (for cleanup) */
export function clearAllLogRuns(): void {
  if (!isDebug) return;
  runs.clear();
  currentRunId = null;
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// =============================================
// PUBLIC — szef widzi (ZAWSZE)
// Etapy, postep, wyniki, czasy, bledy
// =============================================

/** Log visible in production: stage progress, results, errors */
export function logPublic(prefix: string, msg: string, category: LogCategory = "system"): void {
  console.log(`${LOG_PREFIX} [${prefix}] ${msg}`);
  appendEntry({ ts: new Date().toISOString(), level: "PUBLIC", prefix, msg }, category);
}

/** Log error visible in production */
export function logError(prefix: string, msg: string, err?: unknown, category: LogCategory = "system"): void {
  const errMsg = err instanceof Error ? err.message : err ? String(err) : "";
  const fullMsg = `${msg}${errMsg ? " — " + errMsg : ""}`;
  console.error(`${LOG_PREFIX} [${prefix}] ERROR: ${fullMsg}`);
  appendEntry({
    ts: new Date().toISOString(),
    level: "ERROR",
    prefix,
    msg: fullMsg,
    data: err instanceof Error && err.stack ? { stack: err.stack } : undefined,
  }, category);
}

// =============================================
// DEBUG — TYLKO dev (koszty, modele, tokeny, prompty, payloady)
// =============================================

/** Log only in debug mode: costs, models, tokens, prompts, payloads */
export function logDebug(prefix: string, msg: string, data?: Record<string, unknown>, category: LogCategory = "system"): void {
  if (!isDebug) return;
  const dataStr = data ? " " + Object.entries(data).map(([k, v]) => {
    if (typeof v === "string" && v.length > 200) return `${k}=(${v.length} chars)`;
    if (typeof v === "number") return `${k}=${v}`;
    if (typeof v === "boolean") return `${k}=${v}`;
    if (Array.isArray(v)) return `${k}=[${v.length}]`;
    return `${k}=${JSON.stringify(v)}`;
  }).join(", ") : "";
  console.log(`${LOG_PREFIX} [${prefix}] ${msg}${dataStr}`);
  appendEntry({ ts: new Date().toISOString(), level: "DEBUG", prefix, msg, data }, category);
}

/**
 * Log ONLY at VITE_LOG_LEVEL=verbose — for high-volume dumps:
 * full LLM prompts, raw responses, SERP response structure, OpenAI metadata.
 * Gated on isVerbose; delegates to logDebug (which writes to console + IDB).
 *
 * Używaj dla wszystkiego co w debug trybie generuje > ~10 linii per run i jest
 * wartościowe tylko przy granularnym debugowaniu (nie przy smoke testach).
 */
export function logVerbose(prefix: string, msg: string, data?: Record<string, unknown>, category: LogCategory = "system"): void {
  if (!isVerbose) return;
  logDebug(prefix, msg, data, category);
}

/** Log debug warning */
export function logDebugWarn(prefix: string, msg: string, data?: Record<string, unknown>, category: LogCategory = "system"): void {
  if (!isDebug) return;
  const dataStr = data ? " " + JSON.stringify(data) : "";
  console.warn(`${LOG_PREFIX} [${prefix}] ${msg}${dataStr}`);
  appendEntry({ ts: new Date().toISOString(), level: "WARN", prefix, msg, data }, category);
}

/** Log warning visible in production (zawsze widoczne) */
export function logWarn(prefix: string, msg: string, data?: Record<string, unknown>, category: LogCategory = "system"): void {
  console.warn(`${LOG_PREFIX} [${prefix}] WARN: ${msg}${data ? " " + JSON.stringify(data) : ""}`);
  appendEntry({ ts: new Date().toISOString(), level: "WARN", prefix, msg, data }, category);
}

// =============================================
// Category-scoped helpers — convenience dla per-feature loggerów
// =============================================

function makeCategoryLogger(category: LogCategory) {
  return {
    public: (prefix: string, msg: string) => logPublic(prefix, msg, category),
    debug: (prefix: string, msg: string, data?: Record<string, unknown>) =>
      logDebug(prefix, msg, data, category),
    warn: (prefix: string, msg: string, data?: Record<string, unknown>) =>
      logWarn(prefix, msg, data, category),
    error: (prefix: string, msg: string, err?: unknown) =>
      logError(prefix, msg, err, category),
  };
}

export const ceLog = makeCategoryLogger("content-engine");
export const repairLog = makeCategoryLogger("map-repair");
export const auditLog = makeCategoryLogger("audit");
export const existingPostsLog = makeCategoryLogger("existing-posts");
export const publishingLog = makeCategoryLogger("publishing");
export const authLog = makeCategoryLogger("auth");
export const apiLog = makeCategoryLogger("api");
export const uiLog = makeCategoryLogger("ui");

// =============================================
// Backward-compatible wrappers (used by existing code)
// =============================================

/** Log a pipeline stage start/end/error — PUBLIC for START/END/ERROR, DEBUG for details */
export function logStage(
  stage: string,
  status: "START" | "END" | "ERROR" | "SKIP",
  data?: Record<string, unknown>,
): void {
  if (status === "ERROR") {
    logError(stage, "FAILED", data?.error || data?.reason);
  } else if (status === "START" || status === "END") {
    // Public: just stage + status
    logPublic(stage, status);
    // Debug: full data
    if (data) logDebug(stage, status, data);
  } else {
    logDebug(stage, `SKIP`, data);
  }
}

/** Log a DB operation — DEBUG only (except errors → public) */
export async function logDb(
  operation: "PATCH" | "INSERT" | "SELECT" | "DELETE" | "UPSERT",
  table: string,
  nodeId: string,
  payload: Record<string, unknown>,
  result: { error: any; status?: number; data?: any },
): Promise<void> {
  if (result.error) {
    logError("DB", `${operation} ${table} node=${nodeId.slice(0, 8)} FAILED: ${result.error.message || "unknown"}`);
    logDebug("DB", "Error details", {
      error_details: result.error.details,
      error_hint: result.error.hint,
      error_code: result.error.code,
    });
  } else {
    logDebug("DB", `${operation} ${table} node=${nodeId.slice(0, 8)} OK`, payload);
  }
}

/** Log an LLM call — DEBUG only (costs, tokens, models) */
export function logLlm(
  step: string,
  config: { model: string; provider: string; temperature?: number | null; chaining?: boolean },
  result: { tokensInput?: number; tokensOutput?: number; costUsd?: number; durationMs?: number; responseLength?: number },
): void {
  // Public: just step + duration
  if (result.durationMs) {
    logPublic(step, `${(result.durationMs / 1000).toFixed(1)}s`);
  }
  // Debug: full details
  logDebug(step, `${config.model} — tokens: ${result.tokensInput || "?"}→${result.tokensOutput || "?"}, $${(result.costUsd || 0).toFixed(4)}, ${result.durationMs || "?"}ms${config.chaining ? ", chaining=true" : ""}${result.responseLength ? `, response=${result.responseLength} chars` : ""}`, config as any);
}

/** Log an integrity check — DEBUG for pass, PUBLIC for fail */
export function logIntegrity(
  check: string,
  passed: boolean,
  details?: Record<string, unknown>,
): void {
  if (passed) {
    logDebug("INTEGRITY", `${check}: PASS`, details);
  } else {
    logPublic("INTEGRITY", `${check}: FAIL`);
    logDebug("INTEGRITY", `${check}: FAIL`, details);
  }
}

/** Log writing profile usage — PUBLIC (name only), DEBUG (full details) */
export function logProfile(
  stage: string,
  profile: { author_identity?: string; voice_pronouns?: string; claim_strength?: string; sentence_length?: string } | null,
): void {
  if (!profile) {
    logPublic("PROFILE", `${stage}: domyslny profil`);
  } else {
    logPublic("PROFILE", `${stage}: "${profile.author_identity || "unnamed"}"`);
    logDebug("PROFILE", `${stage} details`, {
      voice: profile.voice_pronouns,
      claim: profile.claim_strength,
      sentences: profile.sentence_length,
    } as any);
  }
}
